import React from "react";
import { getAvailableActions } from "../utils/getAvailableActions";
import {
  X,
  Download,
  MessageSquare,
  FolderTree,
  Trash2,
  RotateCcw,
  CheckCircle,
  Clock,
  XCircle,
} from "lucide-react";
import chatIcon from "../../../assets/UserIcons/NewChatIcon2.svg";

export const RepoFloatingActions = ({
  selectedCount,
  role,
  showChatWithSelectedFiles,
  hideDownloadForSelected,
  hidePendingForSelected,
  onClearSelection,
  onBulkDownload,
  onChatSelectedFiles,
  onOpenMoveModal,
  onOpenDeleteModal,
  onBulkRestore,
  onBulkApprovalStatus,
  selectedItems = [],
  repoView = "global",
  onOpenRenameModal,
  allItems = [],
}) => {
  if (selectedCount === 0) return null;
  const singleSelectedItem = selectedCount === 1 ? selectedItems[0] : null;

  const selectedAvailableActions = selectedItems.map((item) =>
    getAvailableActions({
      item,
      role,
      repoView,
      ui: "floating",
      selectionCount: selectedCount,
      items: allItems,
    }),
  );

  const singleSelectedAvailableActions =
    selectedCount === 1 && singleSelectedItem
      ? getAvailableActions({
          item: singleSelectedItem,
          role,
          repoView,
          ui: "floating",
          selectionCount: 1,
          items: allItems,
        })
      : [];

  const hasAction = (action) =>
    selectedAvailableActions.some((actions) => actions.includes(action));

  const hasSingleAction = (action) =>
    singleSelectedAvailableActions.includes(action);

  const canDownloadSelected = hasAction("download");
  const canChatSelected =
    selectedItems.length > 0 &&
    selectedAvailableActions.every((actions) => actions.includes("chat"));

  const canMoveSelected = hasAction("move");

  const canDeleteSelected = hasAction("delete");

  const canChangeStatusSelected =
    selectedCount === 1 && hasSingleAction("changeStatus");

  const hasRenameAction =
    selectedCount === 1 &&
    hasSingleAction("rename") &&
    Boolean(onOpenRenameModal);

  const canRestoreSelected = hasAction("restoreFile");

  const canTrainSelected = selectedCount === 1 && hasSingleAction("trainFile");

  const canApproveSelected = hasAction("approve");

  const canPendingSelected = hasAction("pending");

  const canRejectedSelected = hasAction("rejected");

  const hasActions =
    canChatSelected ||
    canDownloadSelected ||
    hasRenameAction ||
    canMoveSelected ||
    canChangeStatusSelected ||
    canTrainSelected ||
    canDeleteSelected ||
    canRestoreSelected ||
    canApproveSelected ||
    canPendingSelected ||
    canRejectedSelected;

  const hasFiles = selectedItems.some((item) => item?.type !== "folder");
  const hasFolders = selectedItems.some((item) => item?.type === "folder");

  let chatTitle;

  if (hasFiles && hasFolders) {
    chatTitle = "Chat with selected files and folders";
  } else if (hasFolders) {
    chatTitle =
      selectedItems.length === 1
        ? "Chat with selected folder"
        : "Chat with selected folders";
  } else {
    chatTitle =
      selectedItems.length === 1
        ? "Chat with selected file"
        : "Chat with selected files";
  }

  return (
    <div className="repo-floating-actions">
      <div className="repo-floating-left">
        <button
          className="repo-floating-close"
          onClick={onClearSelection}
          title="Clear selection"
        >
          <X size={16} className="icon-image" />
        </button>
        <span className="repo-floating-count">{selectedCount} selected</span>
      </div>
      <div
        style={{
          width: "1px",
          height: "24px",
          background: "rgba(255,255,255,0.2)",
          marginRight: "4px",
          marginLeft: "8px",
        }}
      />
      <div className="repo-floating-right">
        {!hasActions ? (
          <span className="ak-text-lg">No actions to perform</span>
        ) : (
          <>
            {canChatSelected && showChatWithSelectedFiles && (
              <button
                className="repo-floating-btn"
                onClick={onChatSelectedFiles}
                title={chatTitle}
              >
                <img
                  src={chatIcon}
                  alt="Chat"
                  width={16}
                  height={16}
                  className="icon-image"
                  style={{ width: "18px", height: "18px" }}
                />
                {/* <MessageSquare size={16} /> */}
              </button>
            )}
            {canDownloadSelected && !hideDownloadForSelected && (
              <button
                className="repo-floating-btn"
                onClick={onBulkDownload}
                title="Download"
              >
                <Download size={16} className="icon-image" />
              </button>
            )}

            {hasRenameAction && (
              <button
                className="repo-floating-btn"
                onClick={onOpenRenameModal}
                title="Rename"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="icon-image"
                >
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" />
                </svg>
              </button>
            )}

            {canMoveSelected && (
              <button
                className="repo-floating-btn"
                onClick={onOpenMoveModal}
                title="Move"
              >
                <FolderTree size={16} className="icon-image" />
              </button>
            )}

            {canTrainSelected && (
              <button
                className="repo-floating-btn"
                onClick={() => selectedItems.length && onBulkRestore?.()}
                title="Train File"
              >
                <MessageSquare size={16} className="icon-image" />
              </button>
            )}

            {canDeleteSelected && (
              <button
                className="repo-floating-btn"
                onClick={onOpenDeleteModal}
                title="Delete"
                style={{ color: "var(--danger-color)" }}
              >
                <Trash2 size={16} className="" />
              </button>
            )}

            {canRestoreSelected && (
              <button
                className="repo-floating-btn"
                onClick={onBulkRestore}
                title="Restore File"
                style={{ color: "#4ade80" }}
              >
                <RotateCcw size={16} className="icon-image" />
              </button>
            )}

            {(canApproveSelected ||
              canPendingSelected ||
              canRejectedSelected) && (
              <>
                <div
                  style={{
                    width: "1px",
                    height: "24px",
                    background: "rgba(255,255,255,0.2)",
                    margin: "0 4px",
                  }}
                />

                {canApproveSelected && (
                  <button
                    className="repo-floating-btn"
                    onClick={() => onBulkApprovalStatus("approved")}
                    title="Approve"
                    style={{ color: "#4ade80" }}
                  >
                    <CheckCircle size={16} className="" />
                  </button>
                )}

                {/*   {canPendingSelected && !hidePendingForSelected && (
                  <button
                    className="repo-floating-btn"
                    onClick={() => onBulkApprovalStatus("pending")}
                    title="Set Pending"
                    style={{ color: "#fbbf24" }}
                  >
                    <Clock size={16} className="" />
                  </button>
                )} */}

                {canRejectedSelected && (
                  <button
                    className="repo-floating-btn"
                    onClick={() => onBulkApprovalStatus("rejected")}
                    title="Reject"
                    style={{ color: "#f87171" }}
                  >
                    <XCircle size={16} className="" />
                  </button>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};
